import React, { useEffect, useState } from 'react';
import { Cpu } from 'lucide-react';
import { Agent, OfficeId } from '../../lib/types';
import { STATIC_AGENTS, getOffice } from '../../lib/config';
import { apiListAgents } from '../../lib/api';

interface TeamGridProps {
  officeId: OfficeId;
  onSelectAgent: (agentId: string) => void;
}

const STATUS_COLORS = {
  active: '#27C93F',
  idle: '#FFBD2E',
  blocked: '#FF5F56',
  offline: '#9CA3AF',
};

const MODEL_COLORS: Record<string, string> = {
  haiku: '#3498DB',
  sonnet: '#9B59B6',
  opus: '#E74C3C',
};

/**
 * TeamGrid — grid of specialist agents assigned to this office.
 * Click an agent avatar to open their workstation.
 * Agents are loaded from GitHub API with STATIC_AGENTS as fallback.
 */
export default function TeamGrid({ officeId, onSelectAgent }: TeamGridProps) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiListAgents()
      .then(({ agents: remoteAgents }) => {
        const officeAgents = remoteAgents.filter(
          (a: any) => a.name && (
            a.name.toLowerCase() === officeId ||
            a.type?.includes(officeId) ||
            a.officeId === officeId
          )
        );

        // Merge with static agents for this office
        const staticForOffice = STATIC_AGENTS.filter((a) => a.officeId === officeId);
        const merged = [...staticForOffice];
        officeAgents.forEach((ra: any) => {
          if (!merged.some((m) => m.name === ra.name)) {
            merged.push({ ...ra, officeId, status: 'active' });
          }
        });
        setAgents(merged.length > 0 ? merged as Agent[] : staticForOffice as Agent[]);
      })
      .catch(() => {
        const staticForOffice = STATIC_AGENTS.filter((a) => a.officeId === officeId);
        setAgents(staticForOffice as Agent[]);
      })
      .finally(() => setLoading(false));
  }, [officeId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="h-6 w-6 rounded-full border-2 border-gray-200 border-t-gray-600 animate-spin" />
      </div>
    );
  }

  const office = getOffice(officeId);

  return (
    <div className="flex flex-col h-full" data-testid={`team-grid-${officeId}`}>
      <div className="px-6 py-4 border-b border-gray-100 shrink-0">
        <p className="text-xs text-gray-500">{agents.length} agents — click to open workstation</p>
      </div>
      <div className="flex-1 overflow-auto p-6">
        {agents.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <Cpu size={32} className="text-gray-300 mb-3" />
            <p className="text-sm">No agents assigned to this office</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {agents.map((agent) => (
              <AgentCard
                key={agent.name}
                agent={agent}
                officeColor={office?.color}
                onClick={() => onSelectAgent(agent.name)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Agent Card ────────────────────────────────────────────────────────────────

function AgentCard({
  agent, officeColor, onClick,
}: {
  agent: Agent; officeColor?: string; onClick: () => void;
}) {
  const statusColor = STATUS_COLORS[(agent.status || 'idle') as keyof typeof STATUS_COLORS] || '#9CA3AF';
  const modelColor = MODEL_COLORS[agent.model?.toLowerCase() || 'sonnet'] || '#9B59B6';

  const skills = Array.isArray(agent.skills) ? agent.skills : [];

  return (
    <button
      onClick={onClick}
      className="group flex flex-col items-center p-4 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-[0.98] text-center"
      data-testid={`agent-card-${agent.name}`}
    >
      {/* Avatar */}
      <div className="relative mb-3">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-bold shadow-sm"
          style={{ backgroundColor: officeColor || '#BDC3C7' }}
        >
          {agent.name.charAt(0)}
        </div>
        {/* Status dot */}
        <div
          className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white"
          style={{ backgroundColor: statusColor }}
          title={agent.status || 'idle'}
        />
      </div>

      {/* Name */}
      <p className="text-sm font-semibold text-gray-900 truncate w-full">{agent.name}</p>

      {/* Role */}
      <p className="text-[10px] text-gray-500 mt-0.5 truncate w-full">{agent.type?.replace(/_/g, ' ')}</p>

      {/* Model badge */}
      <span
        className="mt-2 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white"
        style={{ backgroundColor: modelColor }}
      >
        {agent.model || 'sonnet'}
      </span>

      {/* Skills preview */}
      {skills.length > 0 && (
        <p className="mt-2 text-[9px] text-gray-400 truncate w-full">
          {skills.slice(0, 2).join(' · ')}
          {skills.length > 2 && ` +${skills.length - 2}`}
        </p>
      )}
    </button>
  );
}
